import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/services/api';
import { MetroProgress } from '@/components/checkout/MetroProgress';
import { loadRazorpayScript, openRazorpayCheckout } from '@/utils/razorpay';

interface PaymentData {
  razorpay_order_id: string;
  amount: number;
  currency: string;
  key: string;
  local_order_id: string;
}

interface CustomerDetails {
  shippingAddress: string;
  phoneNumber: string;
  customerId: string;
}

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { user } = useAuth();

  const state = location.state as { paymentData: PaymentData; customerDetails: CustomerDetails } | null;
  const paymentData = state?.paymentData;
  const customerDetails = state?.customerDetails;
  const isVerifyingRef = useRef(false);

  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'verifying' | 'success' | 'failed' | 'error'>('pending');
  const [verificationMessage, setVerificationMessage] = useState('');
  const hasOpened = useRef(false);

  // Verify payment with backend
  const verifyPayment = useCallback(async (response: any) => {
    if (isVerifyingRef.current) return;
    if (!user?.isAuthenticated) {
  throw new Error('Session expired. Please login again.');
}
    isVerifyingRef.current = true;
    setVerificationStatus('verifying');

    try {
      const verificationRequest = {
        orderId: paymentData?.local_order_id,
        razorpayOrderId: response.razorpay_order_id,
        razorpayPaymentId: response.razorpay_payment_id,
        razorpaySignature: response.razorpay_signature
      };

      const verifyResponse = await authFetch('/api/orders/verify-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(verificationRequest)
      });

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json().catch(() => ({ error: 'Server error' }));
        throw new Error(errorData.error || `Server error ${verifyResponse.status}`);
      }

      const data = await verifyResponse.json();

      if (data.status === 'success') {
        await clearCart();
        setVerificationStatus('success');
        setVerificationMessage('Payment verified and order confirmed');

        // Redirect to order confirmation after 2 seconds
        setTimeout(() => {
          navigate('/order-confirmation', {
            state: { orderId: paymentData?.local_order_id }
          });
        }, 2000);
      } else {
        setVerificationStatus('failed');
        setVerificationMessage(data.message || 'Payment verification failed');
      }
    } catch (error: any) {
      console.error('Payment verification error:', error);
      setVerificationStatus('error');
      setVerificationMessage(error.message || 'Verification failed');
    }
  }, [paymentData, clearCart, navigate]);

  // Open Razorpay payment
  const openRazorpay = useCallback(async () => {
    if (!paymentData || hasOpened.current) return;

    hasOpened.current = true;

    try {
      // Use absolute URL for logo in development to avoid mixed content issues
      const logoUrl = window.location.hostname === 'localhost' 
        ? undefined // Skip logo in localhost development
        : '/JoyAura.png'; // Use relative path in production

      const response = await openRazorpayCheckout({
        key: paymentData.key,
        amount: paymentData.amount,
        currency: paymentData.currency,
        name: 'JoyAura',
        description: 'Your JoyAura Order',
        ...(logoUrl && { image: logoUrl }), // Only include image if logoUrl is defined
        order_id: paymentData.razorpay_order_id,
        prefill: {
          email: user?.email || 'customer@example.com',
          contact: customerDetails?.phoneNumber || ''
        },
        theme: {
          color: '#E53935'
        }
      });

      if (response.success && response.data) {
        console.log('Razorpay payment successful:', response.data);
        verifyPayment(response.data);
      } else if (response.dismissed) {
        console.log('Payment cancelled by user');
        setVerificationStatus('failed');
        setVerificationMessage('Payment was cancelled');
      } else if (response.error) {
        console.error('Razorpay payment failed:', response.error);
        setVerificationStatus('error');
        setVerificationMessage(
          response.error.description ||
          'Payment failed. Please try again.'
        );
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      setVerificationStatus('error');
      setVerificationMessage(error.message || 'Payment failed. Please try again.');
    }
  }, [paymentData, customerDetails, user, verifyPayment]);

  useEffect(() => {
    if (!user?.isAuthenticated) {
      navigate('/cart');
    }
  }, [user, navigate]);

  // Initialize payment
  useEffect(() => {
    const initializePayment = async () => {
      if (!paymentData || verificationStatus !== 'pending') return;
      try {
        const scriptLoaded = await loadRazorpayScript();
        if (scriptLoaded) {
          openRazorpay();
        } else {
          setVerificationStatus('error');
          setVerificationMessage('Failed to load payment gateway');
        }
      } catch (error) {
        setVerificationStatus('error');
        setVerificationMessage('Failed to initialize payment');
      }
    };

    initializePayment();
  }, [paymentData, openRazorpay, verificationStatus]);

  // Handle missing payment data
  if (!paymentData) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <MetroProgress steps={['Cart', 'Review', 'Payment']} currentStep={2} />

          <div className="max-w-md mx-auto text-center mt-8">
            <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Payment Error</h1>
            <p className="text-muted-foreground mb-6">
              Payment session expired. Please try again.
            </p>
            <Button onClick={() => navigate('/cart')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Cart
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const formatAmount = (paise: number) => {
    return (paise / 100).toFixed(2);
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case 'success':
        return (
          <div className="text-center">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-600 mb-2">Payment Successful!</h2>
            <p className="text-xl font-semibold mb-2">
              Amount Paid
            </p>
            <p className="text-muted-foreground mb-4">{verificationMessage}</p>
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Redirecting to order confirmation...</span>
            </div>
          </div>
        );

      case 'failed':
      case 'error':
        return (
          <div className="text-center">
            <XCircle className="h-20 w-20 text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-destructive mb-2">Payment Failed</h2>
            <p className="text-muted-foreground mb-6">{verificationMessage}</p>
            <div className="space-y-3">
              <Button
                onClick={() => {
                  isVerifyingRef.current = false;
                  hasOpened.current = false;
                  navigate('/review-order');
                }}
                className="w-full"
              >

                Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate('/cart')} className="w-full">
                Back to Cart
              </Button>
            </div>
          </div>
        );

      case 'verifying':
        return (
          <div className="text-center">
            <Loader2 className="h-20 w-20 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Verifying Payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment</p>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <Loader2 className="h-20 w-20 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Loading Payment...</h2>
            <p className="text-muted-foreground">Please wait while we prepare your payment</p>
          </div>
        );
    }
  };

  return (
    <Layout>
      <MetroProgress steps={['Cart', 'Review', 'Payment']} currentStep={2} />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          {/* Order details */}
          <div className="bg-card rounded-lg p-4 mb-6 border">
            <div className="text-center text-sm text-muted-foreground">
              Order: {paymentData.local_order_id}
            </div>
          </div>

          {/* Status content */}
          <div className="bg-card rounded-lg p-8 border">
            {renderContent()}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentPage;